<script setup>
    import { nextTick, onBeforeUnmount, ref, watch } from 'vue'

    const props = defineProps({
        open: {
            type: Boolean,
            required: true
        },
        title: {
            type: String,
            required: false
        }
    });

    const emit = defineEmits(['update:open']);

    const overlay = ref(null);

    const close = () => {
        emit('update:open', false);
    }

    const onKeydown = (e) => {
        if (e.key === 'Escape') {
            close();
        }
    }

    // The overlay's z-index (100050) clears the PhotoSwipe lightbox (100000) and its
    // comments panel (100001), so modals opened from the gallery show on top.

    // While open: lock page scrolling, close on Esc and move focus into the dialog.
    watch(() => props.open, (isOpen) => {
        document.body.classList.toggle('overflow-hidden', isOpen);
        if (isOpen) {
            document.addEventListener('keydown', onKeydown);
            nextTick(() => overlay.value?.focus());
        } else {
            document.removeEventListener('keydown', onKeydown);
        }
    });

    onBeforeUnmount(() => {
        document.body.classList.remove('overflow-hidden');
        document.removeEventListener('keydown', onKeydown);
    });
</script>

<template>
    <Teleport to="body">
        <Transition name="modal" :duration="300">
            <div v-if="open" ref="overlay" class="fixed inset-0 z-[100050] overflow-x-hidden overflow-y-auto bg-black/80 outline-0" role="dialog" aria-modal="true" :aria-label="title" tabindex="-1" @click.self="close">
                <div class="modal-dialog relative flex items-center w-auto m-2 min-h-[calc(100%-1rem)] pointer-events-none sm:max-w-[500px] sm:mx-auto sm:my-7 sm:min-h-[calc(100%-3.5rem)]">
                    <div class="modal-content isolate pointer-events-auto">
                        <div class="flex items-start justify-between p-4">
                            <h5 class="mb-0 leading-normal">
                                <slot name="title">{{ title }}</slot>
                            </h5>
                            <button type="button" class="-mt-4 -mr-4 -mb-4 ml-auto p-4 bg-transparent border-0 text-[1.5rem] font-bold leading-none text-white opacity-50 hover:opacity-75 focus:opacity-75" aria-label="Close" @click="close">
                                <span aria-hidden="true">&times;</span>
                            </button>
                        </div>
                        <slot></slot>
                    </div>
                </div>
            </div>
        </Transition>
    </Teleport>
</template>

<style>
    .modal-enter-active,
    .modal-leave-active {
        transition: opacity .15s linear;
    }
    .modal-enter-active .modal-dialog,
    .modal-leave-active .modal-dialog {
        transition: transform .3s ease-out;
    }
    .modal-enter-from,
    .modal-leave-to {
        opacity: 0;
    }
    .modal-enter-from .modal-dialog,
    .modal-leave-to .modal-dialog {
        transform: translateY(-50px);
    }
</style>
